import {
    Box, Button, CircularProgress, Typography
} from '@mui/material';
import {
    makeStyles
} from '@mui/styles';
import {
    useContext, useEffect, useMemo, useRef, useState
} from 'react';
import SunnysideButton, {
    ButtonSizes, ButtonVariants
} from '../../../components_sunnyside/button';
import {
    FormContext
} from '../../../contexts/form';
import {
    TypographyVariants,
    TypographyWeights
} from '../../../figmaTheme';

export type CardContainerProps = {
    sectionKey: string,
    children: any,
    buttonDelay?: number,

    backgroundColor?: string,
    headerText?: string,
    headerTextColor?: string,
    mainColor?: string,
    arrowColor?: string,
    ctaBackgroundColor?: string,
    // CTA button background color
    ctaButtonColor?: string,
    ctaButtonTextColor?: string,
    disabledButtonColor?: string,
    ctaTextColor?: string,
    ctaButtonStyle?: React.CSSProperties,
    ctaButtonTextStyle?: React.CSSProperties,
    ctaButtonIcon?: {
        position: 'left' | 'right',
        element: JSX.Element
    },
    ctaTextStyle?: React.CSSProperties,

    headerLogo?: JSX.Element,
    buttonText?: string,
    ctaText?: string,
    goNext?: any,
    url?: string,
    backJump?: number,
    goBack?: (jump?: number) => void,
    ctaOnClick?: () => void,
    hideHeader?: boolean,
    hideHeaderText?: boolean,
    hasArrowButtonBorder?: boolean,
    hasGradient?: boolean,
    shiftUp?: boolean,
    hideArrowButton?: boolean,
    showCTAButton?: boolean,
    isDisabled?: boolean,
    hideLogo?: boolean,
    showSecondaryCTA?: boolean,
    isButtonLoading?: boolean,
    isSecondaryCTAButtonLoading?: boolean
};

export const CardContainer = ({
    sectionKey,
    children,
    buttonDelay = 0,

    backgroundColor,
    headerText,
    headerTextColor,
    mainColor,
    arrowColor,
    ctaBackgroundColor,
    ctaButtonColor,
    ctaButtonTextColor,
    disabledButtonColor,
    ctaTextColor,
    ctaButtonStyle,
    ctaButtonTextStyle,
    ctaButtonIcon,
    ctaTextStyle,

    headerLogo,
    buttonText,
    ctaText,
    goNext,
    url = '',
    backJump,
    goBack,
    ctaOnClick,
    hideHeader = false,
    hideHeaderText = false,
    hasArrowButtonBorder = false,
    hasGradient = false,
    shiftUp = false,
    hideArrowButton = false,
    showCTAButton = false,
    isDisabled = false,
    hideLogo = false,
    showSecondaryCTA = false,
    isButtonLoading = false,
    isSecondaryCTAButtonLoading = false
}: CardContainerProps): JSX.Element => {
    const useStyles = makeStyles((theme) => ({
        background: {
            position: 'sticky',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            top: 0,
            height: '100vh',
            overflow: 'clip',
            margin: '0 auto',
            scrollbarWidth: 'none'
        },
        headerContainer: {
            width: '100%',
            display: 'flex',
            margin: '0 auto',
            maxWidth: '692px',
            paddingTop: shiftUp ? '0px' : '24px'
        },
        backButton: {
            position: 'absolute',
            marginLeft: '24px',
            marginTop: '4px',
            cursor: 'pointer',
            transform: 'translate(-17px, -17px)'
        },
        header: {
            fontWeight: TypographyWeights.SEMI_BOLD,
            textAlign: 'center',
            paddingBottom: '16px',
            margin: '0 auto'
        },
        logoContainer: {
            margin: '0 auto 10px',
            height: '36px',
            [theme.breakpoints.down('sm')]: {
                height: '32px'
            }
        },
        bottomBlock: {
            pointerEvents: 'none',
            position: 'absolute',
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            padding: '0 24px',
            height: '172px',
            width: '100%',
            maxWidth: '692px',
            zIndex: 2
        },
        gradientBlock: {
            pointerEvents: 'none',
            position: 'absolute',
            bottom: 0,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '80px',
            width: '100%',
            background: `linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, ${theme.palette.white} 75%)`,
            padding: '0 24px',
            maxWidth: '692px',
            zIndex: 2
        },
        arrowButton: {
            padding: '16px',
            borderRadius: '50%',
            placeSelf: 'end',
            minWidth: 0,
            lineHeight: 0,
            position: 'absolute',
            bottom: '24px',
            pointerEvents: 'none',
            cursor:' default',
            opacity: 0,
            animation: `$fadeIn ${buttonDelay ? '0.5s' : '0s'} ease-in-out ${buttonDelay}ms forwards, $makeInteractive 0s ease-in-out ${buttonDelay}ms forwards`
        },
        ctaBox: {
            padding: '16px 24px 24px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            rowGap: '8px',
            width: '100%',
            zIndex: 2,
            position: 'absolute',
            bottom: 0
        },
        ctaButton: {
            width: '100%',
            pointerEvents: 'none',
            opacity: 0,
            animation: `$fadeIn ${buttonDelay ? '0.5s' : '0s'} ease-in-out ${buttonDelay}ms forwards, $makeInteractive 0s ease-in-out ${buttonDelay}ms forwards`
        },
        ctaText: {
            textAlign: 'center',
            pointerEvents: 'auto',
        },
        linkButton: {
            width: '100%',
            pointerEvents: 'auto',
            padding: '16px',
            borderRadius: '16px'
        },
        linkText: {
            fontWeight: TypographyWeights.MEDIUM,
            textAlign: 'center'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: '0'
            },
            '100%': {
                opacity: '1'
            },
        },
        '@keyframes makeInteractive': {
            '0%': {
                pointerEvents: 'none',
                cursor:' default'
            },
            '100%': {
                pointerEvents: 'auto',
                cursor:' pointer'
            },
        }
    }));
    const formContext = useContext(FormContext);
    const classes = useStyles();
    const ctaBoxRef = useRef(null);

    const initialScreenWidth = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth;
        }
        return 0;
    }, []);

    const initialScreenHeight = useMemo(() => {
        if (typeof window !== 'undefined') {
            return window.innerHeight;
        }
        return 0;
    }, []);

    useEffect(() => {
        if (ctaBoxRef.current) {
            setCTABoxHeight(ctaBoxRef.current.clientHeight);
        }
    }, [ctaBoxRef.current, buttonText, ctaText]);

    const [screenWidth, setScreenWidth] = useState(initialScreenWidth);
    const [screenHeight, setScreenHeight] = useState(initialScreenHeight);
    const [CTABoxHeight, setCTABoxHeight] = useState(0);
    const section = useMemo(() => {
        return formContext?.sections.find(s => s.key === sectionKey);
    }, [sectionKey, formContext?.sections]);

    const onNextClick = () => {
        if (!goNext) return;
        goNext();
    };

    const onBackClick = () => {
        if (!goBack) return;
        goBack(backJump);
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const resizeListener = () => {
            setScreenWidth(window.innerWidth);
            setScreenHeight(window.innerHeight);
        };
        window.addEventListener('resize', resizeListener);

        return () => {
            window.removeEventListener('resize', resizeListener);
        };
    }, []);

    const blockClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
    };

    if (!screenWidth || !screenHeight) return <></>;

    return (
        <Box
            className={classes.background}
            style={{
                backgroundColor: (backgroundColor || section?.colors.background),
                height: `${screenHeight}px`
            }}
        >
            {!hideHeader && (
                <Box className={classes.headerContainer}>
                    {goBack && (
                        <svg
                            className={classes.backButton}
                            onClick={onBackClick}
                            width="50"
                            height="50"
                            viewBox="-17 -17 50 50"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M11.7657 1.03432C12.0781 1.34674 12.0781 1.85327 11.7657 2.16569L5.93137 8L11.7657 13.8343C12.0781 14.1467 12.0781 14.6533 11.7657 14.9657C11.4533 15.2781 10.9467 15.2781 10.6343 14.9657L4.23431 8.56569C3.9219 8.25327 3.9219 7.74674 4.23431 7.43432L10.6343 1.03432C10.9467 0.721898 11.4533 0.721898 11.7657 1.03432Z"
                                fill={headerTextColor || section?.colors.headerText}
                            />
                        </svg>
                    )}
                    {(headerText || section?.headerText) && (
                        <Typography
                            variant={TypographyVariants.H4}
                            className={classes.header}
                            style={{
                                color: hideHeaderText ? 'transparent' : (headerTextColor || section.colors.headerText)
                            }}
                        >
                            {headerText || section.headerText}
                        </Typography>
                    )}
                    {(headerLogo || section?.headerLogo) && (
                        <Box className={classes.logoContainer}>
                            {hideLogo ? (
                                <Box
                                    style={{
                                        height: '37px'
                                    }}
                                />
                            ) : (
                                headerLogo || section?.headerLogo
                            )}
                        </Box>
                    )}
                </Box>
            )}
            {children}
            {!hideArrowButton && (
                <Box className={hasGradient ? classes.gradientBlock : classes.bottomBlock}>
                    {goNext && (
                        <Button
                            className={classes.arrowButton}
                            aria-label="Go forward"
                            style={{
                                backgroundColor: isDisabled ? (disabledButtonColor || section?.colors.disabledButton) : (mainColor || section?.colors.main),
                                border: hasArrowButtonBorder ? isDisabled ? `1px solid ${disabledButtonColor || section?.colors.disabledButton}` : `1px solid ${section?.colors.buttonArrow}` : 'none',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                pointerEvents: isDisabled ? 'none' : 'auto'
                            }}
                            onClick={!isDisabled ? onNextClick : null}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 17 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M8.70503 14.095C8.43166 13.8216 8.43166 13.3784 8.70503 13.105L13.1101 8.69999L2.2 8.69999C1.8134 8.69999 1.5 8.38659 1.5 7.99999C1.5 7.61339 1.8134 7.29999 2.2 7.29999L13.1101 7.29999L8.70503 2.89496C8.43166 2.62159 8.43166 2.17838 8.70503 1.90501C8.97839 1.63164 9.42161 1.63164 9.69498 1.90501L15.295 7.50501C15.5683 7.77838 15.5683 8.22159 15.295 8.49496L9.69497 14.095C9.42161 14.3683 8.97839 14.3683 8.70503 14.095Z"
                                    fill={arrowColor || section?.colors.buttonArrow}
                                />
                            </svg>
                        </Button>
                    )}
                </Box>
            )}
            {showCTAButton && (
                <Box
                    className={hasGradient ? classes.gradientBlock : classes.bottomBlock}
                    style={{
                        transform: `translate(0, -${CTABoxHeight}px)`
                    }}
                >
                    <Box
                        ref={ctaBoxRef}
                        className={classes.ctaBox}
                        style={{
                            backgroundColor: (ctaBackgroundColor || section?.colors.ctaBackground),
                            transform: `translate(-24px, ${CTABoxHeight}px)`
                        }}
                    >
                        {buttonText && (
                            <>
                                {url ? (
                                    <a
                                        href={isDisabled ? undefined : url}
                                        onClick={isDisabled ? blockClick : undefined}
                                        rel="noopener noreferrer"
                                        className={classes.linkButton}
                                        style={{
                                            backgroundColor: isDisabled ? (disabledButtonColor || section?.colors.disabledButton) : (ctaButtonColor || section?.colors.ctaButton),
                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                            pointerEvents: isDisabled ? 'none' : 'auto'
                                        }}
                                    >
                                        <Typography
                                            variant={TypographyVariants.H4}
                                            className={classes.linkText}
                                            style={{
                                                color: (ctaButtonTextColor || section?.colors.ctaButtonText)
                                            }}
                                        >
                                            {buttonText}
                                        </Typography>
                                    </a>
                                ) : (
                                    <>
                                        {onNextClick && (
                                            <SunnysideButton
                                                buttonVariant={ButtonVariants.PRIMARY}
                                                buttonSize={ButtonSizes.HUGE}
                                                aria-label={buttonText}
                                                onClick={!isDisabled ? onNextClick : null}
                                                className={classes.ctaButton}
                                                style={{
                                                    backgroundColor: isDisabled ? (disabledButtonColor || section?.colors.disabledButton) : (ctaButtonColor || section?.colors.ctaButton),
                                                    color: (ctaButtonTextColor || section?.colors.ctaButtonText),
                                                    cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                    pointerEvents: isDisabled ? 'none' : 'auto',
                                                    ...(ctaButtonStyle || {})
                                                }}
                                            >
                                                {isButtonLoading ? (
                                                    <CircularProgress
                                                        color='inherit'
                                                        size='1.5rem'
                                                    />
                                                ) : (
                                                    <>
                                                        {ctaButtonIcon && ctaButtonIcon.position === 'left' && ctaButtonIcon.element}
                                                        <Typography
                                                            style={{
                                                                ...(ctaButtonTextStyle || {})
                                                            }}
                                                        >
                                                            {buttonText}
                                                        </Typography>
                                                        {ctaButtonIcon && ctaButtonIcon.position === 'right' && ctaButtonIcon.element}
                                                    </>
                                                )}
                                            </SunnysideButton>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                        {!showSecondaryCTA && ctaText && (
                            <Typography
                                variant={TypographyVariants.SMALL}
                                className={classes.ctaText}
                                style={{
                                    color: ctaTextColor || section?.colors.ctaText,
                                    ...(ctaTextStyle || {})
                                }}
                                onClick={ctaOnClick}
                                dangerouslySetInnerHTML={{
                                    __html: ctaText
                                }}
                            />
                        )}
                        {showSecondaryCTA && (
                            <SunnysideButton
                                buttonVariant={ButtonVariants.TEXT}
                                buttonSize={ButtonSizes.HUGE}
                                onClick={ctaOnClick}
                                className={classes.ctaText}
                                style={{
                                    color: ctaTextColor || section?.colors.ctaText
                                }}
                            >
                                {isSecondaryCTAButtonLoading ? (
                                    <CircularProgress
                                        color='inherit'
                                        size='1.5rem'
                                    />
                                ) : (
                                    <Typography>
                                        {ctaText}
                                    </Typography>
                                )}
                            </SunnysideButton>
                        )}
                    </Box>
                </Box>
            )}
            {hideArrowButton && !showCTAButton && hasGradient && (
                <Box className={classes.gradientBlock} />
            )}
        </Box >
    );
};

export default CardContainer;
